import { getTimeZoneCalendarDateStartUtcMilli, getTimeZoneCalendarDateEndUtcMilli } from '../../common/dashboard/formatters';
import type { ExportCmpReportCsvInput } from "../../../graphql/types";
import { normalizeCmpTimeZone } from "../../../utils/cmpTimeZones";

export type ReportDateRangeValues = {
  startDate: string;
  endDate: string;
  compareStartDate: string;
  compareEndDate: string;
};

type ReportDateRangeExportInput = Pick<
  ExportCmpReportCsvInput,
  | "startDateUtcMilli"
  | "endDateUtcMilli"
  | "compareStartDateUtcMilli"
  | "compareEndDateUtcMilli"
>;

const dateInputPattern = /^(\d{4})-(\d{2})-(\d{2})$/;

const parseDateInputValue = (value: string) => {
  const match = dateInputPattern.exec(value);

  if (!match) {
    return null;
  }

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day));

  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return null;
  }

  return { day, month, year };
};

export const createDefaultReportDateRangeValues = (): ReportDateRangeValues => ({
  startDate: "",
  endDate: "",
  compareStartDate: "",
  compareEndDate: "",
});

export type ApplyReportStartDateInput<T extends ReportDateRangeValues> = {
  values: T;
  startDate: string;
};

export const applyReportStartDate = <T extends ReportDateRangeValues>({
  values,
  startDate,
}: ApplyReportStartDateInput<T>): T => {
  const start = toUtcDayStart(startDate);
  const end = toUtcDayStart(values.endDate);

  return {
    ...values,
    startDate,
    // Keep a valid end while text is incomplete so validation can explain it.
    endDate: start !== null && end !== null && start > end ? "" : values.endDate,
  };
};

export type ApplyCompareStartDateInput<T extends ReportDateRangeValues> = {
  values: T;
  compareStartDate: string;
};

export const applyCompareStartDate = <T extends ReportDateRangeValues>({
  values,
  compareStartDate,
}: ApplyCompareStartDateInput<T>): T => {
  const start = toUtcDayStart(compareStartDate);
  const end = toUtcDayStart(values.compareEndDate);

  return {
    ...values,
    compareStartDate,
    compareEndDate:
      start !== null && end !== null && start > end ? "" : values.compareEndDate,
  };
};

export const toUtcDayStart = (value: string) => {
  const parsed = parseDateInputValue(value);

  return parsed
    ? Date.UTC(parsed.year, parsed.month - 1, parsed.day, 0, 0, 0, 0)
    : null;
};

export const toUtcDayEnd = (value: string) => {
  const parsed = parseDateInputValue(value);

  return parsed
    ? Date.UTC(parsed.year, parsed.month - 1, parsed.day, 23, 59, 59, 999)
    : null;
};

export const formatReportDateLong = (value: string): string => {
  const timestamp = toUtcDayStart(value);

  return timestamp === null
    ? "Not set"
    : new Intl.DateTimeFormat("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
        timeZone: "UTC",
      }).format(timestamp);
};

// Preview sheet period line: "August 1 – August 31, 2026". The year shows once
// when both dates share it; different years show both full dates.
export const formatReportPeriod = ({
  startDate,
  endDate,
}: {
  startDate: string;
  endDate: string;
}): string => {
  const start = toUtcDayStart(startDate);
  const end = toUtcDayStart(endDate);

  if (start === null || end === null) {
    return "All dates";
  }

  const sameYear =
    new Date(start).getUTCFullYear() === new Date(end).getUTCFullYear();
  const startText = new Intl.DateTimeFormat("en-US", {
    month: "long",
    day: "numeric",
    ...(sameYear ? {} : { year: "numeric" }),
    timeZone: "UTC",
  }).format(start);

  return `${startText} – ${formatReportDateLong(endDate)}`;
};

// Report sheet and print "Generated" line, e.g. "Oct 22, 2026 · 9:00 AM", in
// the chamber time zone (IF11 L5), not the time zone of the staff computer.
export const formatReportGeneratedLabel = ({
  generatedAtUtcMilli,
  timeZone,
}: {
  generatedAtUtcMilli: number;
  timeZone?: string | null;
}): string => {
  const zone = normalizeCmpTimeZone(timeZone).timeZone;
  const generated = new Date(generatedAtUtcMilli);
  const date = generated.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: zone });
  const time = generated.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: zone });
  return `${date} · ${time}`;
};

export const getReportDateRangeValidationError = (
  values: ReportDateRangeValues,
): string | null => {
  const hasReportStart = Boolean(values.startDate);
  const hasReportEnd = Boolean(values.endDate);

  if (hasReportStart !== hasReportEnd) {
    return "Select both a Report Start Date and a Report End Date, or leave both blank to include all dates.";
  }

  if (hasReportStart && hasReportEnd) {
    const startDateUtcMilli = toUtcDayStart(values.startDate);
    const endDateUtcMilli = toUtcDayEnd(values.endDate);

    if (startDateUtcMilli === null || endDateUtcMilli === null) {
      return "Select the Report Start Date and the Report End Date from the calendar.";
    }

    if (startDateUtcMilli > endDateUtcMilli) {
      return "Report Start Date must be on or before Report End Date.";
    }
  }

  const hasCompareStart = Boolean(values.compareStartDate);
  const hasCompareEnd = Boolean(values.compareEndDate);

  if ((hasCompareStart || hasCompareEnd) && !hasReportStart && !hasReportEnd) {
    return "Choose a report period before adding comparison dates.";
  }

  if (hasCompareStart !== hasCompareEnd) {
    return "Complete both comparison date fields.";
  }

  if (hasCompareStart && hasCompareEnd) {
    const compareStartDateUtcMilli = toUtcDayStart(values.compareStartDate);
    const compareEndDateUtcMilli = toUtcDayEnd(values.compareEndDate);

    if (compareStartDateUtcMilli === null || compareEndDateUtcMilli === null) {
      return "Choose valid comparison start and end dates.";
    }

    if (compareStartDateUtcMilli > compareEndDateUtcMilli) {
      return "Compare To Start Date must be on or before Compare To End Date.";
    }
  }

  return null;
};

export const buildReportDateRangeInput = (
  values: ReportDateRangeValues,
  timeZone = "UTC",
): ReportDateRangeExportInput => {
  const startDateUtcMilli = toUtcDayStart(values.startDate);
  const endDateUtcMilli = toUtcDayEnd(values.endDate);
  const input: ReportDateRangeExportInput = {};

  if (startDateUtcMilli !== null && endDateUtcMilli !== null) {
    input.startDateUtcMilli = getTimeZoneCalendarDateStartUtcMilli(values.startDate, timeZone);
    input.endDateUtcMilli = getTimeZoneCalendarDateEndUtcMilli(values.endDate, timeZone);
  }

  const compareStartDateUtcMilli = toUtcDayStart(values.compareStartDate);
  const compareEndDateUtcMilli = toUtcDayEnd(values.compareEndDate);

  if (compareStartDateUtcMilli !== null && compareEndDateUtcMilli !== null) {
    input.compareStartDateUtcMilli = getTimeZoneCalendarDateStartUtcMilli(values.compareStartDate, timeZone);
    input.compareEndDateUtcMilli = getTimeZoneCalendarDateEndUtcMilli(values.compareEndDate, timeZone);
  }

  return input;
};
